import React from 'react';
import { format, isAfter, isBefore, isSameDay } from 'date-fns';
import { Wallet, Plus, Trash2, DollarSign, ArrowDown, Receipt, AlertCircle, Tag, ArrowRight, ChevronDown, ChevronUp, X, BarChart, Eye, EyeOff } from 'lucide-react';
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc, Timestamp, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import useEvents from '../hooks/useEvents';
import { addEvent } from '../lib/firebase';
import { BudgetBucket, BucketTransaction } from '../types';
import { Link } from 'react-router-dom';

const BUCKET_CATEGORIES = [
  { value: 'groceries', label: 'Groceries', color: 'bg-green-100 text-green-800' },
  { value: 'dining', label: 'Dining Out', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'entertainment', label: 'Entertainment', color: 'bg-purple-100 text-purple-800' },
  { value: 'shopping', label: 'Shopping', color: 'bg-pink-100 text-pink-800' },
  { value: 'transportation', label: 'Transportation', color: 'bg-blue-100 text-blue-800' },
  { value: 'utilities', label: 'Utilities', color: 'bg-gray-100 text-gray-800' },
  { value: 'healthcare', label: 'Healthcare', color: 'bg-red-100 text-red-800' },
  { value: 'personal', label: 'Personal Care', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'gifts', label: 'Gifts', color: 'bg-rose-100 text-rose-800' },
  { value: 'pet', label: 'Pet Expenses', color: 'bg-amber-100 text-amber-800' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-800' }
] as const;

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceBucket: BudgetBucket;
  buckets: BudgetBucket[];
  onTransfer: (targetBucketId: string, amount: number) => Promise<void>;
}

function TransferModal({ isOpen, onClose, sourceBucket, buckets, onTransfer }: TransferModalProps) {
  const [targetBucketId, setTargetBucketId] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const transferAmount = parseFloat(amount);
    if (transferAmount <= 0) {
      setError('Transfer amount must be greater than 0');
      return;
    }

    if (transferAmount > sourceBucket.remainingAmount) {
      setError('Transfer amount exceeds available balance');
      return;
    }

    try {
      await onTransfer(targetBucketId, transferAmount);
      onClose();
    } catch (error) {
      setError('Failed to transfer funds');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Transfer Funds</h3>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              {error}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">From</label>
            <div className="mt-1 p-2 bg-gray-50 rounded-md">
              {sourceBucket.name} (${sourceBucket.remainingAmount.toFixed(2)} available)
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">To</label>
            <select
              value={targetBucketId}
              onChange={(e) => setTargetBucketId(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            >
              <option value="">Select a bucket</option>
              {buckets
                .filter(b => b.id !== sourceBucket.id)
                .map(bucket => (
                  <option key={bucket.id} value={bucket.id}>
                    {bucket.name} (${bucket.remainingAmount.toFixed(2)} available)
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="block w-full pl-7 rounded-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              Transfer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function BudgetBuckets() {
  const [currentView, setCurrentView] = React.useState('calendar');
  const [showTransactionForm, setShowTransactionForm] = React.useState(false);
  const [showTransferModal, setShowTransferModal] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [showHelp, setShowHelp] = React.useState(false);
  const [showForm, setShowForm] = React.useState(false);
  const [showEmptyBuckets, setShowEmptyBuckets] = React.useState(false);
  const { user } = useAuth();
  const { events } = useEvents();
  const [buckets, setBuckets] = React.useState<BudgetBucket[]>([]);

  const [transactionForm, setTransactionForm] = React.useState({
    amount: '',
    description: ''
  });

  React.useEffect(() => {
    if (!user) return;

    const bucketsQuery = query(
      collection(db, 'budgetBuckets'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(bucketsQuery, (snapshot) => {
      const bucketsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BudgetBucket[];
      setBuckets(bucketsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching buckets:', error);
      setError('Failed to load budget buckets');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const filteredBuckets = React.useMemo(() => {
    return showEmptyBuckets 
      ? buckets 
      : buckets.filter(bucket => Math.abs(bucket.remainingAmount) > 0.001); // Use small epsilon for floating point comparison
  }, [buckets, showEmptyBuckets]);

  const handleAddTransaction = async (e: React.FormEvent, bucketId: string) => {
    e.preventDefault();
    if (!user || !showTransactionForm) return;

    try {
      const bucket = buckets.find(b => b.id === bucketId);
      if (!bucket) return;

      const amount = parseFloat(transactionForm.amount);
      if (amount > bucket.remainingAmount) {
        setError('Transaction amount exceeds remaining budget');
        return;
      }

      const transaction = {
        id: crypto.randomUUID(),
        amount: -amount,
        description: transactionForm.description,
        date: Timestamp.now()
      };

      const bucketRef = doc(db, 'budgetBuckets', bucketId);
      await updateDoc(bucketRef, {
        remainingAmount: bucket.remainingAmount - amount,
        transactions: [...bucket.transactions, transaction]
      });

      setTransactionForm({ amount: '', description: '' });
      setShowTransactionForm(false);
    } catch (error) {
      console.error('Error adding transaction:', error);
      setError('Failed to add transaction');
    }
  };

  const handleDeleteTransaction = async (bucketId: string, transaction: BucketTransaction) => {
    if (!user) return;

    try {
      const bucket = buckets.find(b => b.id === bucketId);
      if (!bucket) return;

      const isTransferFrom = transaction.description.startsWith('Transfer to ');
      const isTransferTo = transaction.description.startsWith('Transfer from ');

      if (isTransferFrom || isTransferTo) {
        let otherBucketName, otherTransactionDescription;
        if (isTransferFrom) {
          otherBucketName = transaction.description.replace('Transfer to ', '');
          otherTransactionDescription = `Transfer from ${bucket.name}`;
        } else {
          otherBucketName = transaction.description.replace('Transfer from ', '');
          otherTransactionDescription = `Transfer to ${bucket.name}`;
        }

        const otherBucket = buckets.find(b => b.name === otherBucketName);
        if (otherBucket) {
          const otherTransaction = otherBucket.transactions.find(t => 
            t.description === otherTransactionDescription &&
            Math.abs(t.amount) === Math.abs(transaction.amount)
          );

          if (otherTransaction) {
            const batch = writeBatch(db);

            const newRemainingAmount = bucket.remainingAmount - transaction.amount;
            const updatedTransactions = bucket.transactions.filter(t => t.id !== transaction.id);
            batch.update(doc(db, 'budgetBuckets', bucketId), {
              remainingAmount: newRemainingAmount,
              transactions: updatedTransactions
            });

            const otherNewRemainingAmount = otherBucket.remainingAmount - otherTransaction.amount;
            const otherUpdatedTransactions = otherBucket.transactions.filter(t => t.id !== otherTransaction.id);
            batch.update(doc(db, 'budgetBuckets', otherBucket.id), {
              remainingAmount: otherNewRemainingAmount,
              transactions: otherUpdatedTransactions
            });

            await batch.commit();
            return;
          }
        }
      }

      const newRemainingAmount = bucket.remainingAmount - transaction.amount;
      const updatedTransactions = bucket.transactions.filter(t => t.id !== transaction.id);

      const bucketRef = doc(db, 'budgetBuckets', bucketId);
      await updateDoc(bucketRef, {
        remainingAmount: newRemainingAmount,
        transactions: updatedTransactions
      });
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setError('Failed to delete transaction');
    }
  };

  const handleTransfer = async (targetBucketId: string, amount: number) => {
    if (!user || !showTransferModal) return;

    try {
      const sourceBucket = buckets.find(b => b.id === showTransferModal);
      const targetBucket = buckets.find(b => b.id === targetBucketId);
      if (!sourceBucket || !targetBucket) throw new Error('Invalid buckets');

      const batch = writeBatch(db);

      const sourceRef = doc(db, 'budgetBuckets', showTransferModal);
      batch.update(sourceRef, {
        remainingAmount: sourceBucket.remainingAmount - amount,
        transactions: [...sourceBucket.transactions, {
          id: crypto.randomUUID(),
          amount: -amount,
          description: `Transfer to ${targetBucket.name}`,
          date: Timestamp.now()
        }]
      });

      const targetRef = doc(db, 'budgetBuckets', targetBucketId);
      batch.update(targetRef, {
        remainingAmount: targetBucket.remainingAmount + amount,
        transactions: [...targetBucket.transactions, {
          id: crypto.randomUUID(),
          amount: amount,
          description: `Transfer from ${sourceBucket.name}`,
          date: Timestamp.now()
        }]
      });

      await batch.commit();
      setShowTransferModal(null);
    } catch (error) {
      console.error('Error transferring funds:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent flex items-center">
            <Wallet className="w-8 h-8 mr-3 text-blue-600" />
            Budget Buckets
          </h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowEmptyBuckets(!showEmptyBuckets)}
              className="flex items-center px-4 py-2 glass-effect text-slate-700 rounded-lg hover:scale-105 transition-all duration-300"
            >
              {showEmptyBuckets ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Hide Empty Buckets
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Show All Buckets
                </>
              )}
            </button>
            <Link
              to="/spending-report"
              className="flex items-center px-4 py-2 gradient-primary text-white rounded-lg hover:scale-105 transition-all duration-300 shadow-lg cyber-glow"
            >
              <BarChart className="w-4 h-4 mr-2" />
              View Spending Report
            </Link>
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center px-4 py-2 gradient-primary text-white rounded-lg hover:scale-105 transition-all duration-300 shadow-lg cyber-glow"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Bucket
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 glass-effect border-l-4 border-red-500 p-4 rounded-lg animate-fadeIn">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBuckets.map((bucket) => {
            const categoryInfo = BUCKET_CATEGORIES.find(cat => cat.value === bucket.category);
            
            return (
              <div
                key={bucket.id}
                className="glass-effect rounded-xl overflow-hidden hover:scale-105 transition-all duration-300 cyber-glow animate-fadeIn"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">{bucket.name}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${categoryInfo?.color}`}>
                        <Tag className="w-3 h-3 mr-1" />
                        {categoryInfo?.label}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowTransferModal(bucket.id)}
                        className="p-2 rounded-lg glass-effect hover:scale-110 transition-all duration-300 text-blue-600"
                        title="Transfer funds"
                      >
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-4 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50">
                      <span className="text-sm font-medium text-slate-600">Available Balance:</span>
                      <span className="font-bold text-2xl bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                        ${bucket.remainingAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4">
                    {showTransactionForm === bucket.id ? (
                      <form onSubmit={(e) => handleAddTransaction(e, bucket.id)} className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Amount</label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              value={transactionForm.amount}
                              onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                              className="block w-full pl-7 rounded-lg border-slate-300 focus:ring-blue-500 focus:border-blue-500 focus:ring-2 transition-all"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Description</label>
                          <input
                            type="text"
                            value={transactionForm.description}
                            onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                            className="mt-1 block w-full rounded-lg border-slate-300 focus:ring-blue-500 focus:border-blue-500 focus:ring-2 transition-all"
                            required
                          />
                        </div>

                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => setShowTransactionForm(false)}
                            className="px-4 py-2 text-sm glass-effect text-slate-700 rounded-lg hover:scale-105 transition-all duration-300"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 text-sm gradient-primary text-white rounded-lg hover:scale-105 transition-all duration-300 shadow-lg"
                          >
                            Add
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button
                        onClick={() => setShowTransactionForm(bucket.id)}
                        className="w-full flex items-center justify-center px-4 py-2 glass-effect rounded-lg text-sm font-medium text-slate-700 hover:scale-105 transition-all duration-300"
                      >
                        <ArrowDown className="w-4 h-4 mr-2" />
                        Record Spending
                      </button>
                    )}
                  </div>

                  {bucket.transactions.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Recent Transactions</h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {bucket.transactions
                          .sort((a, b) => {
                            // Convert Firestore Timestamp to Date for comparison
                            const dateA = a.date instanceof Timestamp ? a.date.toDate() : new Date(a.date);
                            const dateB = b.date instanceof Timestamp ? b.date.toDate() : new Date(b.date);
                            return dateB.getTime() - dateA.getTime();
                          })
                          .map((transaction) => (
                            <div
                              key={transaction.id}
                              className="flex items-center justify-between py-2 border-t border-gray-100 text-sm group"
                            >
                              <div className="flex items-center">
                                <Receipt className="w-4 h-4 text-gray-400 mr-2" />
                                <span className="text-gray-600">{transaction.description}</span>
                              </div>
                              <div className="flex items-center">
                                <span className={`font-medium ${
                                  transaction.description === 'Added Funds' || 
                                  transaction.description.startsWith('Transfer from') ||
                                  Math.abs(transaction.amount) === transaction.amount
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}>
                                  {transaction.description === 'Added Funds' || 
                                   transaction.description.startsWith('Transfer from') ||
                                   Math.abs(transaction.amount) === transaction.amount
                                    ? '+'
                                    : '-'}
                                  ${Math.abs(transaction.amount).toFixed(2)}
                                </span>
                                <button
                                  onClick={() => handleDeleteTransaction(bucket.id, transaction)}
                                  className="ml-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove transaction"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {filteredBuckets.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Wallet className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {showEmptyBuckets ? 'No budget buckets' : 'No buckets with available balance'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {showEmptyBuckets 
                  ? 'Get started by adding your first bucket'
                  : (
                    <>
                      No buckets with available balance found.{' '}
                      <button
                        onClick={() => setShowEmptyBuckets(true)}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        Show all buckets
                      </button>
                    </>
                  )
                }
              </p>
            </div>
          )}
        </div>

        {showTransferModal !== null && (
          <TransferModal
            isOpen={true}
            onClose={() => setShowTransferModal(null)}
            sourceBucket={buckets.find(b => b.id === showTransferModal)!}
            buckets={buckets}
            onTransfer={handleTransfer}
          />
        )}
      </div>
    </div>
  );
}